/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useState } from 'react'
import { AsyncThunk } from '@reduxjs/toolkit/dist/createAsyncThunk'
import dayjs from 'dayjs'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import {
  AddIcon,
  CancelIcon,
  CorrectIcon,
  CredentialIcon,
  DeleteIcon,
  EditIcon,
  IncorrectIcon,
  MarkAsSecondaryIcon,
  SearchIcon,
  SubmitIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import { useAppDispatch } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { License } from 'src/v3/core/types'
import { Tooltip } from '@mui/material'
import ConfirmAction from './confirm-action'

type Domain = 'manage' | 'profile'
type SortOption = 'name-az' | 'name-za' | 'date-new' | 'date-old'

let deleteLicense = (id: string) =>
  axios.delete(`/api/profile/license?licenseID=${id}`)

function prepareLicenses(
  licenses: License[],
  licenseSearch: string,
  licenseSort: SortOption,
): License[] {
  return licenses
    .filter((l) => l.type.toLowerCase().includes(licenseSearch.toLowerCase()))
    .sort((a, b) => {
      if (licenseSort === 'name-az') {
        return a.type.localeCompare(b.type || '') || 0
      }
      if (licenseSort === 'name-za') {
        return b.type.localeCompare(a.type || '') || 0
      }
      if (licenseSort === 'date-old') {
        return dayjs(a.createdTime).diff(b.createdTime)
      }
      return dayjs(b.createdTime).diff(a.createdTime)
    })
}

export let LicenseList = ({
  licenses,
  fetchLicenses,
  setErrorMessage,
  userID,
}: {
  licenses: License[]
  fetchLicenses: AsyncThunk<void, void, object>
  setErrorMessage: (value: React.SetStateAction<string>) => void
  userID?: string
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [licenseSearch, setLicenseSearch] = useState<string>('')
  let [licenseSort, setLicenseSort] = useState<SortOption>('date-new')
  let [licenseTab, setLicenseTab] = useState<string>('active')
  let [deleting, setDeleting] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let domain: Domain = userID ? 'manage' : 'profile'

  let filteredLicenses = [...licenses]

  if (licenseTab === 'active') {
    filteredLicenses = licenses.filter(
      (c) => !dayjs(c.expiration).isBefore(dayjs()),
    )
  } else if (licenseTab === 'expired') {
    filteredLicenses = licenses.filter((c) =>
      dayjs(c.expiration).isBefore(dayjs()),
    )
  } else {
    filteredLicenses = [...licenses]
  }

  filteredLicenses = prepareLicenses(
    filteredLicenses,
    licenseSearch,
    licenseSort,
  )

  let form = useFormik({
    validationSchema: yup.object({
      type: yup.string().required(),
      expiration: yup.string().required(),
      state: yup.string().required(),
      number: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      type: '',
      expiration: '',
      number: '',
      state: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post(`/api/${domain}/license`, {
            userID,
            type: values.type,
            expiration: values.expiration,
            number: values.number,
            state: values.state,
          })
          .then(() => dispatch(fetchLicenses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating License')
            dispatch(fetchLicenses())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put(`/api/${domain}/license`, {
            id: values.id,
            userID,
            type: values.type,
            expiration: values.expiration,
            number: values.number,
            state: values.state,
          })
          .then(() => dispatch(fetchLicenses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating License')
            dispatch(fetchLicenses())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  let onEdit = (id: string) => {
    let licenseEdit = licenses.find((c) => c.id === id)
    form.setFieldValue('id', licenseEdit?.id)
    form.setFieldValue('expiration', licenseEdit?.expiration)
    form.setFieldValue('number', licenseEdit?.number)
    form.setFieldValue('type', licenseEdit?.type)
    form.setFieldValue('state', licenseEdit?.state)
    setFormOpen(true)
  }

  let onFlagSecondary = (license: License) => {
    axios
      .put(`/api/${domain}/license/display-rank`, {
        id: license.id,
        isSecondary: license.displayRank !== 'Secondary',
      })
      .then(() => dispatch(fetchLicenses()))
      .catch((err) => {
        setErrorMessage(err?.message || 'Error setting license to secondary')
        dispatch(fetchLicenses())
        setFormOpen(false)
        setSaving(false)
      })
  }

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap-reverse', mt: '-6px' }}>
        <Tabs
          variant="scrollable"
          value={licenseTab}
          onChange={(e, num) => setLicenseTab(num)}
        >
          <Tab value="active" label="Active" />
          <Tab value="expired" label="Expired" />
          <Tab value="all" label="All" />
          <Divider orientation="vertical" flexItem />
        </Tabs>
        <Box style={{ display: 'flex', gap: '2px', padding: '0 8px' }}>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setFormOpen(true)}
          >
            Create
          </Button>
        </Box>
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          marginY: 2,
        }}
      >
        <TextField
          sx={{ width: 240 }}
          variant="outlined"
          size="small"
          label="Search Licenses"
          placeholder="License Type"
          name="license-search"
          type="text"
          value={licenseSearch}
          onChange={(event: React.ChangeEvent) => {
            setLicenseSearch((event.target as HTMLInputElement).value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ width: 160 }}>
          <InputLabel size="small">Sort</InputLabel>
          <Select
            value={licenseSort}
            label="Sort"
            variant="outlined"
            size="small"
            onChange={(event) =>
              setLicenseSort(event.target.value as SortOption)
            }
          >
            <MenuItem value={'date-new'}>Date: Newest</MenuItem>
            <MenuItem value={'date-old'}>Date: Oldest</MenuItem>
            <MenuItem value={'name-az'}>Name: A-Z</MenuItem>
            <MenuItem value={'name-za'}>Name: Z-A</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 15,
          marginBottom: 15,
          overflowY: 'scroll',
          maxHeight: 500,
        }}
      >
        {filteredLicenses.length ? (
          filteredLicenses.map((license) => (
            <Card
              key={license.id}
              variant="outlined"
              sx={{
                p: 2,
                width: 340,
                textAlign: 'center',
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="h6">
                  <CredentialIcon className="mb-1 mr-1" color="primary" />
                  {license.type}
                </Typography>
                <Typography color="text.secondary" variant="subtitle2">
                  {license.state}
                </Typography>
                <Typography color="text.secondary" variant="subtitle2">
                  {license.number}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                {dayjs(license.expiration).isBefore(dayjs()) ? (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      <IncorrectIcon color="error" />
                      <Typography color="error">Expired</Typography>
                    </Box>
                    <Typography>
                      Expired {dayjs(license.expiration).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      <CorrectIcon color="success" />
                      <Typography color={theme.palette.success.main}>
                        Active
                      </Typography>
                    </Box>
                    <Typography>
                      Expires {dayjs(license.expiration).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>
              {deleting === license.id ? (
                <CircularProgress size="28px" sx={{ margin: 'auto' }} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip
                    title={
                      license.displayRank === 'Secondary'
                        ? 'Unmark as Secondary'
                        : 'Mark as Secondary'
                    }
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      color={
                        license.displayRank === 'Secondary'
                          ? 'error'
                          : undefined
                      }
                      disabled={
                        !!licenses.find(
                          (l) =>
                            l.id !== license.id &&
                            l.displayRank === 'Secondary',
                        )
                      }
                      onClick={() => onFlagSecondary(license)}
                    >
                      <MarkAsSecondaryIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onEdit(license.id)}
                    >
                      <EditIcon />
                    </Button>
                  </Tooltip>
                  {domain === 'profile' && (
                    <>
                      <Tooltip title="Delete">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setDeleting(`delete-${license.id}`)}
                        >
                          <DeleteIcon />
                        </Button>
                      </Tooltip>
                      <ConfirmAction
                        executeAction={() =>
                          deleteLicense(license.id).then(() =>
                            dispatch(fetchLicenses()),
                          )
                        }
                        open={deleting}
                        setOpen={setDeleting}
                        openText={`delete-${license.id}`}
                        title="Confirm Deletion"
                        text="Are you sure you want to delete this license?"
                        onFailure={(err) =>
                          setErrorMessage(
                            err.message || 'Error Deleting License',
                          )
                        }
                      />
                    </>
                  )}
                </Box>
              )}
            </Card>
          ))
        ) : (
          <Typography color="text.secondary">
            There are no licenses matching these filters.
          </Typography>
        )}
      </div>
      <Dialog open={formOpen} onClose={() => setFormOpen(false)}>
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <form onSubmit={form.handleSubmit}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              License
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 2,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                sx={{ mt: 1 }}
                variant="standard"
                size="small"
                label="License Number"
                name="number"
                type="text"
                fullWidth
                value={form.values.number}
                onChange={form.handleChange}
                error={form.touched.number && !!form.errors.number}
              />
              <div className="flex justify-center">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    value={dayjs(form.values.expiration)}
                    onChange={(val: any) =>
                      form.setFieldValue(
                        'expiration',
                        dayjs(val).format('MM/DD/YYYY'),
                      )
                    }
                  />
                </LocalizationProvider>
              </div>
              <FormControl fullWidth>
                <InputLabel sx={{ ml: -2 }}>License Type</InputLabel>
                <Select
                  variant="standard"
                  value={form.values.type}
                  label="License Type"
                  name="type"
                  onChange={form.handleChange}
                  error={form.touched.type && !!form.errors.type}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="" />
                  <MenuItem value="Paramedic">Paramedic</MenuItem>
                  <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
                  <MenuItem value="EMR">Emergency Medical Responder</MenuItem>
                  <MenuItem value="CFR">Certified First Responder</MenuItem>
                  <MenuItem value="EMT">EMT</MenuItem>
                  <MenuItem value="EMT-1">EMT-1</MenuItem>
                  <MenuItem value="EMT-B">EMT-B</MenuItem>
                  <MenuItem value="EMT-D">EMT-D</MenuItem>
                  <MenuItem value="AEMT">AEMT</MenuItem>
                  <MenuItem value="EMT-2">EMT-2</MenuItem>
                  <MenuItem value="EMT-CC">EMT-CC</MenuItem>
                  <MenuItem value="Advanced Care Paramedic (Canada Only)">
                    Advanced Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Primary Care Paramedic (Canada Only)">
                    Primary Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Physician">Physician</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ ml: -2 }}>License State</InputLabel>
                <Select
                  variant="standard"
                  value={form.values.state}
                  label="License State"
                  name="state"
                  onChange={form.handleChange}
                  error={form.touched.state && !!form.errors.state}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Antigua and Barbuda">
                    Antigua and Barbuda
                  </MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="The Bahamas">The Bahamas</MenuItem>
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="DHS">DHS</MenuItem>
                  <MenuItem value="District of Columbia">
                    District of Columbia
                  </MenuItem>
                  <MenuItem value="Ecuador">Ecuador</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Great Britain">Great Britain</MenuItem>
                  <MenuItem value="Guam">Guam</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iraq">Iraq</MenuItem>
                  <MenuItem value="Ireland">Ireland</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="New Zealand">New Zealand</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
                  <MenuItem value="Poland">Poland</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                  <MenuItem value="South Africa">South Africa</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="Scotland">Scotland</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Slovakia">Slovakia</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Trinidad and Tobago">
                    Trinidad and Tobago
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
              {saving ? (
                <CircularProgress className="m-auto" size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setFormOpen(false)}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </Paper>
      </Dialog>
    </>
  )
}
